import { useState } from 'react'
import { Loader2, RotateCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { type TranslationState } from '@/features/i18n/api'
import { StatusBadge } from '@/features/i18n/status-badge'
import {
  PLATFORM_LABEL,
  useMessageDetails,
  useRetranslateMessageDetail,
  useSaveMessageDetail,
  type MessageDetail,
  type Platform,
} from '@/features/app-patch-policy/api'
import { errorMessage } from '@/lib/utils'

const ROW_GRID = 'grid grid-cols-[minmax(120px,160px)_100px_1fr_auto] gap-x-3'

/** Deletes one key from a Record immutably — `delete` on a spread copy, not the state itself. */
function omitKey(record: Record<string, string>, key: string): Record<string, string> {
  const next = { ...record }
  delete next[key]
  return next
}

/** Whether a row's draft differs from what the server last returned for it — shared by the
 * approve-all filter and each row's own Save/Approve label. */
function isDirty(detail: MessageDetail, value: string, titleValue: string): boolean {
  return value !== (detail.content ?? '') || titleValue !== (detail.title ?? '')
}

function toTranslationState(detail: MessageDetail): TranslationState {
  if (detail.needs_review) return 'needs_review'
  if (detail.is_manual) return 'human'
  if (detail.content) return 'machine'
  return 'missing'
}

/**
 * Every enabled language's translation of one platform's English patch
 * message, in one dialog — deliberately much smaller than app/i18n's
 * MessageEditor (no placeholders, no module/key fields, no polling: this
 * table's Retranslate is synchronous). A row here is either untouched
 * (content null — ships English), a machine draft, or is_manual (a person
 * saved it — permanently exempt from automatic retranslation until someone
 * explicitly regenerates it).
 */
export function MessageTranslationsDialog({
  platform,
  releaseVersion,
  patchNumber,
  englishMessage,
  open,
  onOpenChange,
}: {
  platform: Platform
  releaseVersion: string
  patchNumber: number
  englishMessage: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data, isPending, isError, error } = useMessageDetails(platform, releaseVersion, patchNumber)
  const save = useSaveMessageDetail(platform, releaseVersion, patchNumber)
  const retranslate = useRetranslateMessageDetail(platform, releaseVersion, patchNumber)

  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [titleDrafts, setTitleDrafts] = useState<Record<string, string>>({})
  const [busyLang, setBusyLang] = useState<string | null>(null)
  const [busyAll, setBusyAll] = useState(false)
  const [errorByLang, setErrorByLang] = useState<Record<string, string>>({})

  function draftFor(languageCode: string, serverContent: string | null) {
    return drafts[languageCode] ?? serverContent ?? ''
  }

  function titleDraftFor(languageCode: string, serverTitle: string | null) {
    return titleDrafts[languageCode] ?? serverTitle ?? ''
  }

  // The server values matter for Approve: an untouched machine draft has no entry in drafts, so
  // falling back to the empty string would silently save nothing at all.
  async function saveOne(detail: MessageDetail) {
    const languageCode = detail.language_code
    const content = draftFor(languageCode, detail.content).trim()
    if (!content) return
    setBusyLang(languageCode)
    setErrorByLang((current) => ({ ...current, [languageCode]: '' }))
    try {
      await save.mutateAsync({
        languageCode,
        title: titleDraftFor(languageCode, detail.title).trim(),
        content,
      })
      setDrafts((current) => omitKey(current, languageCode))
      setTitleDrafts((current) => omitKey(current, languageCode))
    } catch (err) {
      setErrorByLang((current) => ({
        ...current,
        [languageCode]: errorMessage(err, 'Could not save.'),
      }))
    } finally {
      setBusyLang(null)
    }
  }

  // Rows that would go live if Approve were clicked on each of them: has content, not already
  // owned by a person, or edited here and not yet saved.
  const pendingRows = (data ?? []).filter((detail) => {
    const value = draftFor(detail.language_code, detail.content)
    if (!value.trim()) return false
    const edited = isDirty(detail, value, titleDraftFor(detail.language_code, detail.title))
    return edited || !detail.is_manual
  })

  // Sequential, not Promise.all: every row hits the same platform's row set and the list refetches
  // after each save, so firing them at once would race the cache against itself.
  async function approveAll() {
    setBusyAll(true)
    for (const detail of pendingRows) {
      await saveOne(detail)
    }
    setBusyAll(false)
  }

  async function retranslateOne(languageCode: string) {
    setBusyLang(languageCode)
    setErrorByLang((current) => ({ ...current, [languageCode]: '' }))
    try {
      await retranslate.mutateAsync(languageCode)
      setDrafts((current) => omitKey(current, languageCode))
    } catch (err) {
      setErrorByLang((current) => ({
        ...current,
        [languageCode]: errorMessage(err, 'Could not retranslate.'),
      }))
    } finally {
      setBusyLang(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-[92vw] max-w-none flex-col sm:max-w-[880px]">
        <DialogHeader>
          <DialogTitle>{PLATFORM_LABEL[platform]} dialog translations</DialogTitle>
          <DialogDescription>
            English (the baseline, edited in step 2 or 3):{' '}
            <span className="text-foreground">{englishMessage || '(empty)'}</span>. Both the
            heading and the message are translated here, and each falls back to its own English
            baseline on its own — a language left blank ships the English one, see
            cwc-backend-v1&apos;s GET /app-patch-status.
            <br />
            <span className="text-warning">
              "Machine" rows are unreviewed drafts — cwc-backend-v1 never serves them to a real
              device. A language only goes live once you hit Save on it here (even unedited);
              that click is the review.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="-mr-2 min-h-0 flex-1 overflow-y-auto pr-2">
          {isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
          {isError && (
            <p className="text-sm text-destructive">{(error as Error).message}</p>
          )}

          {data && (
            <div className="overflow-hidden rounded-lg border border-border">
              <div
                className={`${ROW_GRID} border-b border-border bg-secondary/60 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground`}
              >
                <span>Language</span>
                <span>Status</span>
                <span>Heading &amp; message</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busyAll || busyLang !== null || pendingRows.length === 0}
                  onClick={() => void approveAll()}
                >
                  {busyAll ? 'Approving…' : `Approve all (${pendingRows.length})`}
                </Button>
              </div>

              {data.map((detail) => {
                const busy = busyLang === detail.language_code || busyAll
                const value = draftFor(detail.language_code, detail.content)
                const titleValue = titleDraftFor(detail.language_code, detail.title)
                const dirty = isDirty(detail, value, titleValue)
                return (
                  <div
                    key={detail.language_code}
                    className={`${ROW_GRID} items-start border-b border-border px-3 py-2.5 last:border-b-0`}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-semibold">{detail.display_name}</div>
                      <code className="font-mono text-[10px] text-muted-foreground">
                        {detail.language_code}
                      </code>
                    </div>

                    <div className="pt-0.5">
                      <StatusBadge status={toTranslationState(detail)} />
                    </div>

                    <div className="min-w-0 space-y-1.5">
                      <Input
                        value={titleValue}
                        disabled={busy}
                        onChange={(event) =>
                          setTitleDrafts((current) => ({
                            ...current,
                            [detail.language_code]: event.target.value,
                          }))
                        }
                        aria-label={`${detail.display_name} heading`}
                        placeholder={busy ? 'Working…' : 'Heading — blank ships the English one'}
                      />
                      <Textarea
                        rows={2}
                        value={value}
                        disabled={busy}
                        onChange={(event) =>
                          setDrafts((current) => ({
                            ...current,
                            [detail.language_code]: event.target.value,
                          }))
                        }
                        aria-label={`${detail.display_name} translation`}
                        placeholder={busy ? 'Working…' : 'Not translated — ships the English text'}
                      />
                      {errorByLang[detail.language_code] && (
                        <p className="mt-1 text-[11px] text-destructive">
                          {errorByLang[detail.language_code]}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Button
                        type="button"
                        size="sm"
                        // Enabled on an untouched machine draft too — Save IS
                        // the review, and a correct draft that needs no
                        // editing must still be approvable in one click, not
                        // require a throwaway edit just to un-disable this
                        // button. Only truly nothing-to-do (already
                        // human-approved and untouched) stays disabled.
                        disabled={busy || !value.trim() || (!dirty && detail.is_manual)}
                        onClick={() => void saveOne(detail)}
                      >
                        {!dirty && !detail.is_manual ? 'Approve' : 'Save'}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busy || !englishMessage.trim()}
                        title={
                          detail.is_manual
                            ? `Replace the human ${detail.display_name} translation with a fresh machine draft.`
                            : `Regenerate the ${detail.display_name} draft.`
                        }
                        onClick={() => void retranslateOne(detail.language_code)}
                      >
                        {busy ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          <RotateCw className="size-3" />
                        )}
                        Retranslate
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-border pt-3">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
